import React, { createContext, useContext, useState } from "react";
import algosdk from "algosdk";
import {
  saveEncryptedWallet,
  loadEncryptedWallet,
} from "../utils/secureStorage";

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: 250_000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function encryptMnemonic(mnemonic: string, password: string): Promise<string> {
  const enc = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    enc.encode(mnemonic)
  );

  return JSON.stringify({
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(encrypted))
  });
}

async function decryptMnemonic(encryptedStr: string, password: string): Promise<string> {
  const dec = new TextDecoder();
  const { salt, iv, ciphertext } = JSON.parse(encryptedStr);

  const saltBytes = base64ToBytes(salt);
  const ivBytes = base64ToBytes(iv);
  const ciphertextBytes = base64ToBytes(ciphertext);
  const key = await deriveKey(password, saltBytes);

  const decrypted = await crypto.subtle.decrypt(
  { name: "AES-GCM", iv: new Uint8Array(ivBytes).buffer },
  key,
  new Uint8Array(ciphertextBytes).buffer
);

  return dec.decode(decrypted);
}

interface WalletContextType {
  account: algosdk.Account | null;
  username: string | null;
  setAccount: (acc: algosdk.Account | null, save?: boolean, username?: string) => void;
  restoreSession: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  setPassword: (value: string) => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccountState] = useState<algosdk.Account | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [pendingAccount, setPendingAccount] = useState<algosdk.Account | null>(null);
  const [pendingUser, setPendingUser] = useState<string | null>(null);

  const setPassword = async (password: string) => {
    if (!pendingAccount || !pendingUser) return;
    const mnemonic = algosdk.secretKeyToMnemonic(pendingAccount.sk);
    const encrypted = await encryptMnemonic(mnemonic, password);
    await saveEncryptedWallet(pendingUser, encrypted);
    setAccountState(pendingAccount);
    setUsername(pendingUser);
    setPendingAccount(null);
    setPendingUser(null);
  };

  const setAccount = (acc: algosdk.Account | null, persist = false, uname?: string) => {
  setAccountState(acc);
  if (uname) setUsername(uname);

  if (persist && acc) {
    localStorage.setItem("accountAddress", acc.addr);
  }
};

  const restoreSession = async (username: string, password: string): Promise<boolean> => {
    try {
      const encrypted = await loadEncryptedWallet(username);
      if (!encrypted) return false;
      const mnemonic = await decryptMnemonic(encrypted, password);
      const acc = algosdk.mnemonicToSecretKey(mnemonic);
      setAccountState(acc);
      setUsername(username);
      return true;
    } catch {
      return false;
    }
  };

  const logout = async () => {
    setAccountState(null);
    setUsername(null);
  };

  return (
    <WalletContext.Provider
      value={{ account, username, setAccount, restoreSession, logout, setPassword }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet debe usarse dentro de <WalletProvider>");
  }
  return context;
};